import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api, { errMsg } from '../lib/api';
import { gbp, fmtDate, PAYMENT_STATUS_LABELS } from '../lib/shop';
import { PageHead, Modal, Field, inp } from './components';

const STATUSES = Object.entries(PAYMENT_STATUS_LABELS);

export function PaymentsTable({ provider }) {
  const [payments, setPayments] = useState([]);
  const [status, setStatus] = useState('');
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ status: '', received_crypto_amount: '', transaction_hash: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (status) params.status = status;
      if (provider) params.provider = provider;
      const { data } = await api.get('/admin/payments', { params });
      setPayments(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [status, provider]);

  const open = (p) => setSel(p) || setForm({ status: p.status, received_crypto_amount: p.received_crypto_amount ?? '', transaction_hash: p.transaction_hash || '' });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { status: form.status, transaction_hash: form.transaction_hash };
      if (form.received_crypto_amount !== '') payload.received_crypto_amount = Number(form.received_crypto_amount);
      await api.post(`/admin/payments/${sel.id}/status`, payload);
      toast.success(['successful', 'confirmed'].includes(form.status) ? 'Payment confirmed — order marked paid, stock reduced, customer emailed' : 'Payment updated');
      setSel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <select data-testid="payments-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="border hairline bg-white px-3 py-2.5 text-xs font-mono2 uppercase focus:outline-none">
          <option value="">All statuses</option>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="border-b hairline">
              {['Order', 'Provider', 'Method', 'Amount', 'Status', 'Reference', 'Created', ''].map((h) => (
                <th key={h} className="text-left p-3 font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b hairline last:border-0 hover:bg-[#FAFAF8]">
                <td className="p-3 font-mono2 font-semibold">{p.order?.order_number || p.order_id}</td>
                <td className="p-3 font-mono2 text-xs uppercase">{p.payment_provider || '—'}</td>
                <td className="p-3 font-mono2 text-xs uppercase">{p.payment_method}{p.crypto_network ? ` · ${p.crypto_network}` : ''}</td>
                <td className="p-3 font-mono2">{gbp(p.amount)}{p.expected_crypto_amount ? <span className="block text-[10px] text-[#8A8F95]">{p.expected_crypto_amount} {p.crypto_asset}</span> : ''}</td>
                <td className="p-3"><span className="font-mono2 text-[10px] uppercase tracking-wider border hairline px-2 py-0.5" data-testid={`payment-status-${p.id}`}>{PAYMENT_STATUS_LABELS[p.status]}</span></td>
                <td className="p-3 font-mono2 text-[10px] text-[#8A8F95]">{p.transaction_hash ? `${p.transaction_hash.slice(0, 14)}…` : (p.provider_payment_id || '—')}</td>
                <td className="p-3 text-xs text-[#8A8F95]">{fmtDate(p.created_at)}</td>
                <td className="p-3"><button data-testid={`payment-reconcile-${p.id}`} onClick={() => open(p)} className="border hairline px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-widest hover:border-[#0B0F12]">Review</button></td>
              </tr>
            ))}
            {payments.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[#8A8F95]">No payments</td></tr>}
          </tbody>
        </table>
      </div>

      {sel && (
        <Modal title={`Reconcile payment — ${sel.order?.order_number || ''}`} onClose={() => setSel(null)}>
          <div className="space-y-4">
            <div className="font-mono2 text-xs space-y-1 border hairline p-3 bg-[#F4F4F0]">
              <p>AMOUNT: {gbp(sel.amount)} {sel.currency}</p>
              {sel.crypto_asset && <p>ASSET: {sel.crypto_asset} · {sel.crypto_network}</p>}
              {sel.expected_crypto_amount && <p>EXPECTED: {sel.expected_crypto_amount}</p>}
              {sel.provider_metadata?.receiving_address && <p className="break-all">ADDRESS: {sel.provider_metadata.receiving_address}</p>}
              {sel.transaction_hash && <p className="break-all">TX: {sel.transaction_hash}</p>}
            </div>
            <Field label="Payment status">
              <select data-testid="reconcile-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inp}>
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            {sel.crypto_asset && (
              <Field label="Amount actually received (crypto)">
                <input data-testid="reconcile-received" type="number" step="any" value={form.received_crypto_amount} onChange={(e) => setForm({ ...form, received_crypto_amount: e.target.value })} className={inp} />
              </Field>
            )}
            <Field label="Transaction hash / provider reference">
              <input data-testid="reconcile-tx" value={form.transaction_hash} onChange={(e) => setForm({ ...form, transaction_hash: e.target.value })} className={inp} />
            </Field>
            <button data-testid="reconcile-save" onClick={save} disabled={saving}
              className="w-full bg-[#0B0F12] text-white py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
              {saving ? 'Saving…' : 'Save reconciliation'}
            </button>
            {sel.payment_provider === 'stripe' && ['successful', 'confirmed'].includes(sel.status) && (
              <button data-testid="stripe-refund-button" disabled={saving}
                onClick={async () => {
                  if (!window.confirm(`Refund ${sel.order?.order_number || 'this payment'} in full via Stripe? Stock is restored and the order is marked refunded.`)) return;
                  setSaving(true);
                  try {
                    await api.post(`/admin/payments/${sel.id}/refund`, {});
                    toast.success('Refund issued via Stripe — order refunded, stock restored');
                    setSel(null);
                    load();
                  } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
                }}
                className="w-full border border-[#E54D2E] text-[#E54D2E] py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#E54D2E] hover:text-white transition-colors disabled:opacity-50">
                Refund via Stripe
              </button>
            )}
            <p className="text-[11px] text-[#8A8F95]">Marking Successful/Confirmed marks the order paid, reduces stock and emails the customer. Marking Failed/Cancelled/Expired cancels the unpaid order.</p>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function AdminPayments() {
  return (
    <div data-testid="admin-payments">
      <PageHead title="Payments" sub="All payment records across providers. Reconcile manually where needed." />
      <PaymentsTable />
    </div>
  );
}
